// The video view: player with the marker bar under it, the points of
// interest on the right, the rolling transcript below. Shared by the
// investigation file page and the case editor's footage panel; the owner
// maps its leads/events/findings to VideoPoints and supplies the transcript
// (the investigation transcript component drives off the <video> element).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import InvestigationTranscript, { TranscriptHandle, TranscriptHighlight } from "../../Investigations/InvestigationTranscript";
import { CallParticipant } from "../../../db/schema";
import { NormalizedTranscript } from "../../Investigations/api";
import ResizeHandle from "../ResizeHandle";
import { useResizablePanel } from "../../../hooks/useResizablePanel";
import EventMarkerBar from "./EventMarkerBar";
import PointsOfInterestList from "./PointsOfInterestList";
import VideoPlayer, { VideoPlayerHandle } from "./VideoPlayer";
import { VideoPoint, nextPoint, prevPoint } from "./types";

export type VideoViewHandle = { seek: (ms: number, play?: boolean) => void; focus: (pointId: string) => void };

interface Props {
  src: string | null;
  posterUrl?: string;
  durationMs: number;
  points: VideoPoint[];
  idle?: { start_ms: number; end_ms: number }[];
  transcript: NormalizedTranscript | null;
  participants?: CallParticipant[] | null;
  frameUrl: (t: number) => string;
  initialSeekMs?: number;
  focusPointId?: string | null;
  onLeadStatus?: (leadId: string, status: string, reason?: string) => void;
  onRenameSpeaker?: (label: string) => void;
  transcriptScrollKey?: string;
  listTitle?: string;
  listEmptyText?: string;
  storageKey?: string; // rail width key
  // Rendered above the list, e.g. the watch-for editor or the footage summary.
  aside?: React.ReactNode;
  className?: string;
  onTime?: (ms: number) => void;
}

const VideoView: React.FC<Props> = ({ src, posterUrl, durationMs, points, idle, transcript, participants, frameUrl, initialSeekMs, focusPointId, onLeadStatus, onRenameSpeaker, transcriptScrollKey, listTitle, listEmptyText, storageKey = "pnai-videoRailWidth", aside, className, onTime }) => {
  const playerRef = useRef<VideoPlayerHandle>(null);
  const transcriptRef = useRef<TranscriptHandle>(null);
  const [media, setMedia] = useState<HTMLVideoElement | null>(null);
  const [currentMs, setCurrentMs] = useState(initialSeekMs || 0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [barHover, setBarHover] = useState<Set<string>>(new Set());
  const rail = useResizablePanel({ storageKey, defaultWidth: 360, minWidth: 260, maxWidth: 640, side: "left", maxViewportFraction: 0.5 });

  // The <video> element mounts after the first render; hand it to the transcript.
  useEffect(() => {
    const t = setInterval(() => {
      const el = playerRef.current?.media || null;
      if (el && el !== media) setMedia(el);
    }, 100);
    return () => clearInterval(t);
  }, [media]);

  const seek = useCallback((ms: number, play = true) => {
    playerRef.current?.seek(ms, play);
    transcriptRef.current?.seek(ms, play);
    setCurrentMs(ms);
  }, []);

  const highlights = useMemo<TranscriptHighlight[]>(
    () =>
      points.map((p) => ({
        id: p.id,
        startMs: p.tStartMs,
        endMs: Math.max(p.tEndMs, p.tStartMs + 1000),
        severity: p.severity ?? 3,
        kind: p.kind,
        label: p.title,
        quote: p.description,
        explanation: p.explanation,
        corner: undefined,
        dismissed: p.status === "dismissed",
      })),
    [points]
  );

  // Deep link (?lead= / ?point=): highlight and seek once.
  const focused = useRef<string | null>(null);
  useEffect(() => {
    if (!focusPointId || focused.current === focusPointId) return;
    const p = points.find((x) => x.id === focusPointId);
    if (!p) return;
    focused.current = focusPointId;
    setActiveId(p.id);
    const t = setTimeout(() => seek(p.tStartMs, false), 150);
    return () => clearTimeout(t);
  }, [focusPointId, points, seek]);

  const onPoint = (ms: number, p: VideoPoint) => {
    setActiveId(p.id);
    seek(ms, true);
  };

  return (
    <div className={cn("flex flex-col min-h-0 h-full", className)}>
      <div className="flex min-h-0 flex-1">
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="p-3 shrink-0">
            <VideoPlayer
              ref={playerRef}
              src={src}
              poster={posterUrl}
              durationMs={durationMs}
              initialSeekMs={initialSeekMs}
              onTime={(ms) => {
                setCurrentMs(ms);
                onTime?.(ms);
              }}
              hasPoints={points.length > 0}
              onPrevPoint={() => {
                const p = prevPoint(points, currentMs);
                if (p) onPoint(p.tStartMs, p);
              }}
              onNextPoint={() => {
                const p = nextPoint(points, currentMs);
                if (p) onPoint(p.tStartMs, p);
              }}
              markers={
                <EventMarkerBar
                  overlay
                  points={points}
                  durationMs={durationMs}
                  currentMs={currentMs}
                  idle={idle}
                  activeId={activeId}
                  onHover={(ids) => setBarHover(new Set(ids))}
                  onSeek={onPoint}
                  frameUrl={frameUrl}
                />
              }
            />
          </div>
          <div data-tour="footage-transcript" className="flex-1 min-h-0 border-t border-base-300">
            {transcript && transcript.segments.length > 0 ? (
              <InvestigationTranscript
                ref={transcriptRef}
                transcript={transcript}
                audioUrl=""
                media={media || undefined}
                hideControls
                participants={participants}
                highlights={highlights}
                onRenameSpeaker={onRenameSpeaker}
                scrollKey={transcriptScrollKey}
                frameUrl={frameUrl}
              />
            ) : (
              <p className="p-4 text-sm text-base-content/60">{transcript ? "No speech in this recording." : "This recording has no audio track, so there is no transcript. The points of interest come from the footage alone."}</p>
            )}
          </div>
        </div>
        <div style={{ width: rail.width }} className="relative shrink-0 flex min-h-0 border-l border-base-300 bg-base-200/40">
          <ResizeHandle edge="left" active={rail.isResizing} {...rail.handleProps} />
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {aside}
            <div className="flex-1 min-h-0">
              <PointsOfInterestList
                points={points}
                currentMs={currentMs}
                activeId={activeId}
                hoverIds={barHover}
                onHover={setActiveId}
                onSeek={onPoint}
                frameUrl={frameUrl}
                onLeadStatus={onLeadStatus}
                title={listTitle}
                emptyText={listEmptyText}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoView;
